'use client'

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

const SHARE_BTN =
  'social-share-btn m-0 flex shrink-0 touch-manipulation items-center justify-center rounded-md border-0 bg-gray-100/80 p-0 appearance-none sm:size-8 sm:rounded-lg md:size-9 dark:bg-slate-800/80'
const SHARE_ICON = 'size-4 md:size-5'
const SHARE_STROKE_ICON = `${SHARE_ICON} share-stroke-icon`

type ShareItem = {
  id: string
  label: string
  className: string
  href?: string
  onClick?: () => void
  icon: ReactNode
  /** Keep outside the overflow menu when possible */
  pin?: boolean
  /** Only show on small screens (e.g. native share) */
  mobileOnly?: boolean
}

type Props = {
  title: string
  shareUrl: string
  commentsId?: string
  commentsOpen?: boolean
  commentCount?: number
  canNativeShare?: boolean
  linkCopied?: boolean
  onTrackShare?: () => void
  onCopyLink?: () => void
  onNativeShare?: () => void
}

function ShareControl({
  item,
  onTrackShare,
}: {
  item: ShareItem
  onTrackShare?: () => void
}) {
  const className = `${SHARE_BTN} ${item.className}${
    item.mobileOnly ? ' md:hidden' : ''
  }`

  if (item.href) {
    const isMail = item.href.startsWith('mailto:')
    return (
      <a
        href={item.href}
        {...(!isMail
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : {})}
        onClick={onTrackShare}
        className={className}
        title={item.label}
        aria-label={item.label}
        data-share-id={item.id}
      >
        {item.icon}
      </a>
    )
  }

  return (
    <button
      type="button"
      onClick={item.onClick}
      className={className}
      title={item.label}
      aria-label={item.label}
      data-share-id={item.id}
    >
      {item.icon}
    </button>
  )
}

export default function ArticleShareSocial({
  title,
  shareUrl,
  commentsId,
  commentsOpen,
  commentCount = 0,
  canNativeShare,
  linkCopied,
  onTrackShare,
  onCopyLink,
  onNativeShare,
}: Props) {
  const shareUrlEnc = encodeURIComponent(shareUrl)
  const shareText = encodeURIComponent(title)
  const blueskyText = encodeURIComponent(`${title}\n${shareUrl}`)

  const items = useMemo<ShareItem[]>(() => {
    const list: ShareItem[] = [
      {
        id: 'facebook',
        label: "Facebook'ta Paylaş",
        className: 'social-facebook',
        href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrlEnc}`,
        icon: (
          <svg className={SHARE_ICON} viewBox="0 0 24 24" aria-hidden>
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
        ),
      },
      {
        id: 'x',
        label: "X'te Paylaş",
        className: 'social-x',
        href: `https://x.com/intent/tweet?url=${shareUrlEnc}&text=${shareText}`,
        icon: (
          <svg className={SHARE_ICON} viewBox="0 0 24 24" aria-hidden>
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        ),
      },
      {
        id: 'linkedin',
        label: "LinkedIn'de Paylaş",
        className: 'social-linkedin',
        href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrlEnc}`,
        icon: (
          <svg className={SHARE_ICON} viewBox="0 0 24 24" aria-hidden>
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
        ),
      },
      {
        id: 'whatsapp',
        label: "WhatsApp'ta Paylaş",
        className: 'social-whatsapp',
        href: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${title} ${shareUrl}`)}`,
        icon: (
          <svg className={SHARE_ICON} viewBox="0 0 24 24" aria-hidden>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
          </svg>
        ),
      },
      {
        id: 'telegram',
        label: "Telegram'da Paylaş",
        className: 'social-telegram',
        href: `https://t.me/share/url?url=${shareUrlEnc}&text=${shareText}`,
        icon: (
          <svg className={SHARE_ICON} viewBox="0 0 24 24" aria-hidden>
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
          </svg>
        ),
      },
      {
        id: 'pinterest',
        label: "Pinterest'te Paylaş",
        className: 'social-pinterest',
        href: `https://pinterest.com/pin/create/button/?url=${shareUrlEnc}&description=${shareText}`,
        icon: (
          <svg className={SHARE_ICON} viewBox="0 0 24 24" aria-hidden>
            <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
          </svg>
        ),
      },
      {
        id: 'reddit',
        label: "Reddit'te Paylaş",
        className: 'social-reddit',
        href: `https://www.reddit.com/submit?url=${shareUrlEnc}&title=${shareText}`,
        icon: (
          <svg className={SHARE_ICON} viewBox="0 0 24 24" aria-hidden>
            <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
          </svg>
        ),
      },
      {
        id: 'email',
        label: 'E-posta ile Paylaş',
        className: 'social-email',
        href: `mailto:?subject=${shareText}&body=${encodeURIComponent(`${title}\n\n${shareUrl}`)}`,
        icon: (
          <svg className={`email-icon ${SHARE_ICON}`} viewBox="0 0 24 24" aria-hidden>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        ),
      },
      {
        id: 'bluesky',
        label: "Bluesky'de Paylaş",
        className: 'social-bluesky',
        href: `https://bsky.app/intent/compose?text=${blueskyText}`,
        icon: (
          <svg className={SHARE_ICON} viewBox="0 0 24 24" aria-hidden>
            <path d="M5.202 2.857C7.954 4.922 10.913 9.11 12 11.358c1.087-2.247 4.046-6.436 6.798-8.501C20.783 1.366 24 .213 24 3.883c0 .732-.42 6.156-.667 7.037-.856 3.061-3.978 3.842-6.755 3.37 4.854.826 6.089 3.562 3.422 6.299-5.065 5.196-7.28-1.304-7.847-2.97-.104-.305-.152-.448-.153-.327 0-.121-.05.022-.153.327-.568 1.666-2.782 8.166-7.847 2.97-2.667-2.737-1.432-5.473 3.422-6.3-2.777.473-5.899-.308-6.755-3.369C.42 10.04 0 4.615 0 3.883c0-3.67 3.217-2.517 5.202-1.026" />
          </svg>
        ),
      },
      {
        id: 'copy',
        label: linkCopied ? 'Kopyalandı' : 'Linki kopyala',
        className: 'social-copy',
        pin: true,
        onClick: onCopyLink,
        icon: linkCopied ? (
          <svg className={SHARE_STROKE_ICON} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          <svg className={SHARE_STROKE_ICON} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>
        ),
      },
    ]

    if (canNativeShare) {
      list.push({
        id: 'native',
        label: 'Paylaş',
        className: 'social-native',
        pin: true,
        mobileOnly: true,
        onClick: onNativeShare,
        icon: (
          <svg className={SHARE_STROKE_ICON} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
        ),
      })
    }

    if (commentsOpen && commentsId) {
      list.push({
        id: 'comments',
        label: 'Yorumlar',
        className: 'social-comments',
        pin: true,
        href: `#${commentsId}`,
        icon: (
          <>
            <svg className={SHARE_STROKE_ICON} viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.75}
                d="M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <span className="sr-only">{commentCount} yorum</span>
          </>
        ),
      })
    }

    return list
  }, [
    blueskyText,
    canNativeShare,
    commentCount,
    commentsId,
    commentsOpen,
    linkCopied,
    onCopyLink,
    onNativeShare,
    shareText,
    shareUrl,
    shareUrlEnc,
    title,
  ])

  const wrapRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [overflowIds, setOverflowIds] = useState<string[]>([])
  const [menuOpen, setMenuOpen] = useState(false)

  const recalculate = useCallback(() => {
    const wrap = wrapRef.current
    const measure = measureRef.current
    if (!wrap || !measure) return

    const available = wrap.clientWidth
    if (available <= 0) return

    const styles = getComputedStyle(wrap)
    const gap = parseFloat(styles.columnGap || styles.gap || '4') || 4
    const widths = new Map<string, number>()
    Array.from(measure.querySelectorAll<HTMLElement>('[data-share-id]')).forEach(
      (el) => {
        const id = el.dataset.shareId
        if (!id) return
        // md:hidden items report 0 width when desktop media applies
        const w = el.getBoundingClientRect().width
        if (w > 0) widths.set(id, w)
      },
    )

    const getW = (id: string) => widths.get(id) || 32
    const plusW = widths.get('__plus__') || getW('copy')

    const flexible = items.filter((i) => !i.pin)
    const pinned = items.filter((i) => i.pin && widths.has(i.id))

    const fits = (flexIds: string[], withPlus: boolean) => {
      const parts = [
        ...flexIds.map(getW),
        ...(withPlus ? [plusW] : []),
        ...pinned.map((p) => getW(p.id)),
      ]
      if (parts.length === 0) return true
      const total = parts.reduce((a, b) => a + b, 0) + gap * (parts.length - 1)
      return total <= available + 0.5
    }

    const keep: string[] = []
    for (const item of flexible) {
      const candidate = [...keep, item.id]
      const remaining = flexible.length - candidate.length
      if (fits(candidate, remaining > 0)) {
        keep.push(item.id)
      } else {
        break
      }
    }

    const next = flexible.slice(keep.length).map((i) => i.id)
    setOverflowIds((prev) => {
      if (prev.length === next.length && prev.every((id, i) => id === next[i])) {
        return prev
      }
      return next
    })
  }, [items])

  useLayoutEffect(() => {
    recalculate()
    const wrap = wrapRef.current
    if (!wrap || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => recalculate())
    ro.observe(wrap)
    window.addEventListener('resize', recalculate)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', recalculate)
    }
  }, [recalculate])

  useEffect(() => {
    if (overflowIds.length === 0 && menuOpen) setMenuOpen(false)
  }, [overflowIds, menuOpen])

  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  const overflowSet = useMemo(() => new Set(overflowIds), [overflowIds])
  const visibleFlexible = items.filter((i) => !i.pin && !overflowSet.has(i.id))
  const pinnedItems = items.filter((i) => i.pin)
  const overflowItems = items.filter((i) => overflowSet.has(i.id))

  return (
    <div className="article-share-bar relative z-20 flex w-full min-w-0 max-w-full items-center md:w-auto md:justify-end">
      {/* Hidden measure row */}
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute -left-[9999px] top-0 flex items-center gap-1 sm:gap-1.5 md:gap-2"
        aria-hidden
      >
        {items.map((item) => (
          <ShareControl key={`m-${item.id}`} item={item} />
        ))}
        <span className={`${SHARE_BTN}`} data-share-id="__plus__">
          +
        </span>
      </div>

      <div
        ref={wrapRef}
        className="article-share-social relative flex min-w-0 flex-1 flex-nowrap items-center gap-1 sm:gap-1.5 md:gap-2"
        role="group"
        aria-label="Sosyal medyada paylaş"
      >
        {visibleFlexible.map((item) => (
          <ShareControl
            key={item.id}
            item={item}
            onTrackShare={item.href ? onTrackShare : undefined}
          />
        ))}

        {overflowItems.length > 0 ? (
          <div className="relative z-30 shrink-0" ref={menuRef}>
            <button
              type="button"
              className={`${SHARE_BTN} social-more`}
              title="Daha fazla paylaş"
              aria-label="Daha fazla paylaş"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setMenuOpen((v) => !v)
              }}
            >
              <svg className={SHARE_STROKE_ICON} viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
            {menuOpen ? (
              <ul
                role="menu"
                className="absolute right-0 top-full z-[80] mt-2 min-w-[11rem] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
              >
                {overflowItems.map((item) => (
                  <li key={item.id} role="none">
                    {item.href ? (
                      <a
                        role="menuitem"
                        href={item.href}
                        {...(!item.href.startsWith('mailto:')
                          ? { target: '_blank', rel: 'noopener noreferrer' }
                          : {})}
                        onClick={() => {
                          onTrackShare?.()
                          setMenuOpen(false)
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-slate-800"
                      >
                        <span className={`${SHARE_BTN} ${item.className} !size-7`}>
                          {item.icon}
                        </span>
                        <span className="truncate">
                          {item.label
                            .replace(/'da Paylaş/g, '')
                            .replace(/'de Paylaş/g, '')
                            .replace(/ ile Paylaş/g, '')
                            .trim()}
                        </span>
                      </a>
                    ) : (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          item.onClick?.()
                          setMenuOpen(false)
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-slate-800"
                      >
                        <span className={`${SHARE_BTN} ${item.className} !size-7`}>
                          {item.icon}
                        </span>
                        <span className="truncate">{item.label}</span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {pinnedItems.map((item) => (
          <ShareControl
            key={item.id}
            item={item}
            onTrackShare={item.href?.startsWith('#') ? undefined : onTrackShare}
          />
        ))}
      </div>
    </div>
  )
}
